import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {formatLastMessageTime, RoomWithLastMessage, truncateMessage} from '../../hooks/useMatrix';
import {PhoneHQAvatar, PhoneHQButton, PhoneHQInput} from "ch3-ui-lib";
import {PencilLine, ChevronLeft, Lock, PhoneMissed} from "lucide-react";
import {isMissedIncomingCallKind} from "../../utilis/callRow.ts";
import {GroupAvatarTile, RoomAvatar} from "./RoomAvatar";
import {UserStatus} from "../../store/features/Team/teamService.ts";
import {useIdResolver} from "../../hooks/useIdResolver.ts";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice.ts";
import {getUserStatus} from "../../utilis/getStatus.ts";
import {Search, MessageSquare} from "lucide-react";
import {NewRoomItem} from "./NewRoomItem.tsx";
import { UserDirectoryItem} from "../../pages/Chat/Chat.tsx";
import {MatrixClient, Room, RoomMember} from "matrix-js-sdk";
import _ from "lodash";
import {matrixService} from "../../services/matrixService.ts";
import {formatLastEvent, isRelevantTimelineEvent} from "../../utilis/chatUtils.ts";
import {matchesSearchTerm} from "../../utilis/searchMatch.ts";
import {isServiceParticipant} from "../../utils/aiBotUtils.ts";
import {useFetchUsersQuery, useLazyResolveMatrixIdsQuery, UserElement} from "../../store/features/Users/userService.ts";
import {selectMatrixIdToUserId} from "../../store/features/IdResolver/idResolverSlice.ts";
import {CallInProgressBadge} from '../../features/groupCalls/components/CallInProgressBadge';

interface RoomListProps {
    roomsWithLastMessages: RoomWithLastMessage[];
    selectedRoomId: string | null;
    onSelectRoom: (roomId: string) => void;
    onCreateRoom: () => void;
    onNewConversationClick: (selectedUser: UserDirectoryItem) => void;
    client: MatrixClient | null;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    onLoadMoreRooms?: () => Promise<void> | void;
    hasMoreRooms?: boolean;
}

type AvatarUser = RoomWithLastMessage['otherUsers'][number];

const RoomItem: React.FC<{
    roomWithLastMessage: RoomWithLastMessage;
    isSelected: boolean;
    onClick: () => void;
    statuses: UserStatus[];
    getUserId: (matrixId: string) => string;
    getRoomUserAvatar: (matrixId: string) => string | undefined;
    currentUserAvatar: AvatarUser | null;
}> = ({roomWithLastMessage, isSelected, onClick, statuses, getUserId, getRoomUserAvatar, currentUserAvatar}) => {
    const {lastMessage, otherUsers, unreadCount, roomName, room} = roomWithLastMessage;
    const hasUnread = unreadCount > 0;
    // The one thing on this row that has to survive a glance down a long list:
    // a customer still waiting for a call back. A call the user placed
    // themselves and nobody took owes nothing, so it stays neutral here.
    const isMissedCall = lastMessage?.type === 'call' && !!lastMessage.callKind
        && isMissedIncomingCallKind(lastMessage.callKind);
    const currentUserId = useSelector(selectUserId);
    const currentMatrixUserId = matrixService.getCurrentUserMatrixId();
    const otherUsersWithAvatars = useMemo(() => otherUsers.map(user => ({
        ...user,
        avatarUrl: user.avatarUrl || getRoomUserAvatar(user.userId) || ''
    })), [otherUsers, getRoomUserAvatar]);

    const isDirectMessage = useMemo(() => {
        if (roomWithLastMessage.isLocal) return roomWithLastMessage.isDirect ?? otherUsers.length <= 1;
        if (!room) return otherUsers.length <= 1;
        return matrixService.isDirectMessageRoom(room);
    }, [room, roomWithLastMessage.isLocal, roomWithLastMessage.isDirect, otherUsers.length]);

    // A local echo has no room object yet; it gains the lock once it syncs.
    const isRoomEncrypted = useMemo(
        () => !!room?.hasEncryptionStateEvent?.(),
        [room],
    );

    const groupAvatarUsers = useMemo(() => {
        if (!room) {
            if (isDirectMessage || !currentUserAvatar) return otherUsersWithAvatars;

            const currentUserWithAvatar = {
                ...currentUserAvatar,
                avatarUrl: currentUserAvatar.avatarUrl || getRoomUserAvatar(currentUserAvatar.userId) || ''
            };
            const hasCurrentUser = otherUsersWithAvatars.some(user => user.userId === currentUserWithAvatar.userId);
            return hasCurrentUser ? otherUsersWithAvatars : [currentUserWithAvatar, ...otherUsersWithAvatars];
        }

        const joinedUsers = room
            .getJoinedMembers()
            .filter((member: RoomMember) => member.membership === 'join' && !isServiceParticipant(member.userId))
            .map((member: RoomMember) => {
                const matrixAvatarUrl = member.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 32, 32, 'crop', true, true) || '';

                return {
                    userId: member.userId,
                    displayName: member.rawDisplayName || member.userId,
                    avatarUrl: matrixAvatarUrl || getRoomUserAvatar(member.userId) || ''
                };
            });

        if (!joinedUsers.length) return otherUsersWithAvatars;
        if (!currentMatrixUserId) return joinedUsers;

        const me = joinedUsers.find(user => user.userId === currentMatrixUserId);
        const others = joinedUsers.filter(user => user.userId !== currentMatrixUserId);
        return me ? [me, ...others] : joinedUsers;
    }, [room, otherUsersWithAvatars, currentMatrixUserId, isDirectMessage, currentUserAvatar, getRoomUserAvatar]);

    const getRoomName = () => {
        const fallbackName = otherUsersWithAvatars.map(user => user.displayName).join(', ');
        const isUnresolved = (name: string) =>
            name.includes('chat.chte.ch') || name === 'Empty room';

        if (roomName) {
            return isUnresolved(roomName) ? fallbackName : roomName;
        }

        if (room?.name) {
            return isUnresolved(room.name) ? fallbackName : room.name;
        }

        return fallbackName;
    }
    const roomUsers = useMemo(() => {
        return otherUsersWithAvatars?.reduce((acc: (UserStatus & { avatarUrl: string })[], user: AvatarUser) => {
            const resolvedUserId = getUserId(user.userId);
            const status = statuses.find(status => String(status.userId) === resolvedUserId);

            if (resolvedUserId && resolvedUserId !== String(currentUserId) && status) {
                acc.push({
                    ...status,
                    avatarUrl: user.avatarUrl || '',
                });
            }
            return acc;
        }, []);
    }, [otherUsersWithAvatars, statuses, currentUserId, getUserId]);

    const shouldShowStackedAvatar = !isDirectMessage && groupAvatarUsers.length > 1;
    const singleAvatarUser = isDirectMessage ? otherUsersWithAvatars[0] : (groupAvatarUsers[0] || otherUsersWithAvatars[0]);

    const unreadBadge = hasUnread ? (
        <span
            data-testid="chat.room-list.unread-badge"
            aria-label={`${unreadCount} unread`}
            className={`${isMissedCall ? 'bg-danger' : 'bg-brand'} text-white text-footnote font-semibold rounded-full px-1.5 h-5 min-w-5 box-border inline-flex shrink-0 items-center justify-center`}>
            {unreadCount < 9 ? unreadCount : '9+'}
        </span>
    ) : null;

    return (
        <button
            onClick={(e) => {
                onClick();
                e.currentTarget.blur();
            }}
            aria-current={isSelected ? 'true' : undefined}
            className={`w-full text-left rounded-control transition-colors relative outline-none focus-visible:ring-2 focus-visible:ring-brand-focus ${
                isSelected
                    ? 'bg-brand-tint-subtle' : 'hover:bg-surface-muted'}`}
        >
            <div className="flex h-12 items-center gap-3 px-2">
                <div className="w-8 h-8 shrink-0 flex items-center justify-center font-medium relative">
                    {
                        shouldShowStackedAvatar ?
                            (room?.getMxcAvatarUrl()
                                ? <RoomAvatar room={room} roomUsers={groupAvatarUsers} size="32px" />
                                : <GroupAvatarTile />) :
                            <PhoneHQAvatar size={'32px'} name={singleAvatarUser?.displayName}
                                           status={getUserStatus(roomUsers[0])}
                                           src={singleAvatarUser?.avatarUrl || undefined}/>
                    }
                    {/* Encryption on the room's face, the way presence sits on a
                        person's: two groups can hold the same members and differ
                        only in this. DMs are always encrypted, so a lock on every
                        one of them would be noise. */}
                    {isRoomEncrypted && !isDirectMessage && (
                        <span
                            data-testid="chat.room-list.encrypted-icon"
                            className="absolute -bottom-1 -right-1 rounded-full bg-surface p-[1px] leading-none"
                        >
                            <Lock size={11} className="!p-0 text-success-ink" />
                        </span>
                    )}
                </div>

                <div className="flex-1 min-w-0 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 min-w-0">
                        <p className={`m-0 flex-1 min-w-0 truncate text-callout font-semibold text-ink`}>
                            {getRoomName() || 'Unnamed Room'}
                        </p>
                        {lastMessage?.content && (
                            <span className={`shrink-0 whitespace-nowrap text-footnote font-semibold ${
                                isMissedCall ? 'text-danger-ink' : hasUnread ? 'text-ink-muted-strong' : 'text-ink-muted'
                            }`}>
                                {formatLastMessageTime(lastMessage.timestamp)}
                            </span>
                        )}
                    </div>

                    {lastMessage && (
                        <div className="flex items-center gap-2 min-w-0">
                            {isMissedCall && (
                                <PhoneMissed
                                    size={14}
                                    data-testid="chat.room-list.missed-call-icon"
                                    className="!p-0 shrink-0 mr-1 text-danger-ink"
                                />
                            )}
                            <p className={`m-0 text-footnote truncate flex-1 min-w-0 ${
                                isMissedCall
                                    ? 'text-danger-ink'
                                    : hasUnread
                                        ? 'text-ink-muted-strong'
                                        : 'text-ink-muted'
                            }`}>
                                {lastMessage.type === 'call' ? (
                                    <span className="flex items-center text-sm">
                                        {lastMessage.content}
                                    </span>
                                ) : lastMessage.type === 'event' ? (
                                    <span className="flex items-center text-sm">
                                        {truncateMessage(lastMessage.content)}
                                    </span>
                                ) : lastMessage.type === 'text' ? (roomUsers.length === 1 ? truncateMessage(lastMessage.content) : (truncateMessage(lastMessage.senderDisplayName + ': ' + lastMessage.content))) :
                                    <span className="flex items-center text-sm ">
                                        {lastMessage.content && lastMessage.senderDisplayName + ': '} {lastMessage.content}
                                    </span>
                                }
                            </p>
                            {room && <CallInProgressBadge roomId={room.roomId} compact/>}
                            {unreadBadge}
                        </div>
                    )}

                    {!lastMessage && (
                        <div className="flex items-center gap-2 min-w-0">
                            <p className="m-0 flex-1 text-footnote text-ink-muted">No messages yet</p>
                            {room && <CallInProgressBadge roomId={room.roomId} compact/>}
                            {unreadBadge}
                        </div>
                    )}
                </div>
            </div>

        </button>
    );
};

type RoomFilter = 'all' | 'groups' | 'direct' | 'unreads';

const ROOM_LIST_LOAD_MORE_THRESHOLD_PX = 400;
const ROOM_LIST_LOAD_MORE_BATCH_SIZE = 50;

const RoomList: React.FC<RoomListProps> = ({
                                               roomsWithLastMessages,
                                               selectedRoomId,
                                               onSelectRoom,

                                               onCreateRoom,
                                               client,
                                               onNewConversationClick,
                                               searchTerm,
                                               setSearchTerm,
                                               onLoadMoreRooms,
                                               hasMoreRooms = false,
                                           }) => {

    const [usersWithoutConversation, setUsersWithoutConversation] = useState<UserDirectoryItem[]>([]);
    const [roomsSearchResults, setRoomsSearchResults] = useState<RoomWithLastMessage[]>([]);
    const [inputFocus, setInputFocus] = useState(false);
    const [activeFilter, setActiveFilter] = useState<RoomFilter>('all');
    const [isLoadingMoreRooms, setIsLoadingMoreRooms] = useState(false);
    const roomsListRef = useRef<HTMLDivElement | null>(null);
    const lastLoadRequestRoomCountRef = useRef<number | null>(null);
    const loadRequestTargetRoomCountRef = useRef<number | null>(null);
    const requestedMatrixIdsRef = useRef<Set<string>>(new Set());

    const { getUserId, allStatuses } = useIdResolver();
    const matrixIdToUserIdMap = useSelector(selectMatrixIdToUserId);
    const [resolveMatrixIds] = useLazyResolveMatrixIdsQuery();
    const {data: users = []} = useFetchUsersQuery({});
    const userAvatarById = useMemo(() => {
        const avatars = new Map<string, string>();

        users.forEach((user: UserElement) => {
            if (!user.avatarUrl) return;

            avatars.set(String(user.id), user.avatarUrl);
            avatars.set(String(user.userId), user.avatarUrl);
        });

        return avatars;
    }, [users]);

    const getRoomUserAvatar = useCallback((matrixId: string): string | undefined => {
        const appUserId = getUserId(matrixId);
        return appUserId ? userAvatarById.get(String(appUserId)) : undefined;
    }, [getUserId, userAvatarById]);

    const currentUserAvatar = useMemo<AvatarUser | null>(() => {
        const currentMatrixId = matrixService.getCurrentUserMatrixId();
        if (!currentMatrixId) return null;

        for (const roomWithLastMessage of roomsWithLastMessages) {
            const member = roomWithLastMessage.room?.getMember(currentMatrixId);
            if (!member) continue;
            return {
                userId: currentMatrixId,
                displayName: member.rawDisplayName || currentMatrixId,
                avatarUrl: member.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 32, 32, 'crop', true, true)
                    || getRoomUserAvatar(currentMatrixId)
                    || ''
            };
        }

        return {
            userId: currentMatrixId,
            displayName: currentMatrixId,
            avatarUrl: getRoomUserAvatar(currentMatrixId) || ''
        };
    }, [roomsWithLastMessages, getRoomUserAvatar]);

    async function searchConversations(client: MatrixClient, searchValue: string): Promise<RoomWithLastMessage[]> {

        const rooms = client.getRooms().filter((room: Room) => !room.getJoinedMembers().some((member: RoomMember) => member.rawDisplayName === "phq_admin"));
        const searchTerm = searchValue.trim();
        const myUserId = client.getUserId();

        // Same accent insensitive matching the in-chat search uses, so "Lodz"
        // finds "Łódź" here as well.
        const matchingRooms = rooms.filter((room: Room) => {
            if (room.name && matchesSearchTerm(room.name, searchTerm)) {
                return true;
            }

            const members = room.getJoinedMembers();
            return members.some((member: RoomMember) =>
                Boolean(member.rawDisplayName) && matchesSearchTerm(member.rawDisplayName, searchTerm)
            );
        });

        return matchingRooms.map((room: Room) => {
            const members = room.getJoinedMembers();
            const otherUsers = members
                .filter((member: RoomMember) => member.userId !== myUserId)
                .map((member: RoomMember) => ({
                    userId: member.userId,
                    displayName: member.rawDisplayName || member.userId,
                    avatarUrl: member.getAvatarUrl(window.__ENV__?.SYNAPSE_BASE_URL, 32, 32, 'crop', true, true) || ''
                }));

            const unreadCount = matrixService.getUnreadMessageCount(room.roomId);

            const timeline = room.getLiveTimeline();
            const events = timeline.getEvents();
            const relevantEvents = events.filter(isRelevantTimelineEvent);
            const lastEvent = relevantEvents.length > 0 ? relevantEvents[relevantEvents.length - 1] : null;
            const lastMessage = lastEvent ? formatLastEvent(lastEvent, room) : null;

            return {
                room,
                roomId: room.roomId,
                roomName: room.name,
                otherUsers,
                unreadCount,
                lastMessage,
                isLocal: false,
                isDirect: matrixService.isDirectMessageRoom(room)
            };
        });
    }
    const debouncedAPISearch = useCallback(
        _.debounce(async (value: string) => {
            if(!client) return;
            setUsersWithoutConversation([]);
            setRoomsSearchResults([]);

            const result = await searchConversations(client, value);
            setRoomsSearchResults(result);

            if (!result.length) {
                const searchResults = await client.searchUserDirectory({
                    term: value,
                });

                const filteredUsers = searchResults?.results.filter(user => {
                    return user.user_id !== client.getUserId();
                });

                setUsersWithoutConversation(filteredUsers);
            }
        }, 600),
        [client]
    );

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        debouncedAPISearch(value);
    };

    useEffect(() => {
        if (!searchTerm) {
            setRoomsSearchResults([]);
            setUsersWithoutConversation([]);
            setInputFocus(false);
        }
    }, [searchTerm]);

    const filterRooms = useCallback((rooms: RoomWithLastMessage[]) => {
        if (activeFilter === 'all') return rooms;

        return rooms.filter((roomWithLastMessage) => {
            const room = roomWithLastMessage.room;
            const unreadCount = roomWithLastMessage.unreadCount;
            const isLocal = roomWithLastMessage.isLocal;

            if (activeFilter === 'unreads') {
                return unreadCount > 0;
            }

            const isDirectMessage = isLocal
                ? (roomWithLastMessage.isDirect ?? (roomWithLastMessage.otherUsers?.length || 0) <= 1)
                : (room ? matrixService.isDirectMessageRoom(room) : (roomWithLastMessage.otherUsers?.length || 0) <= 1);

            if (activeFilter === 'direct') {
                return isDirectMessage;
            }

            if (activeFilter === 'groups') {
                return !isDirectMessage;
            }

            return true;
        });
    }, [activeFilter]);

    const filteredRooms = useMemo(() =>
        filterRooms(roomsWithLastMessages),
        [roomsWithLastMessages, filterRooms]
    );

    const displayedRooms = useMemo(
        () => searchTerm ? roomsSearchResults : filteredRooms,
        [searchTerm, roomsSearchResults, filteredRooms]
    );

    useEffect(() => {
        const unresolvedMatrixIds = new Set<string>();
        const queueMatrixId = (matrixId: string) => {
            if (
                Object.prototype.hasOwnProperty.call(matrixIdToUserIdMap, matrixId) ||
                requestedMatrixIdsRef.current.has(matrixId)
            ) {
                return;
            }

            unresolvedMatrixIds.add(matrixId);
        };

        displayedRooms.forEach(roomWithLastMessage => {
            roomWithLastMessage.otherUsers.forEach(user => {
                queueMatrixId(user.userId);
            });

            roomWithLastMessage.room?.getJoinedMembers().forEach((member: RoomMember) => {
                queueMatrixId(member.userId);
            });
        });

        if (unresolvedMatrixIds.size) {
            const matrixIds = Array.from(unresolvedMatrixIds);
            matrixIds.forEach(matrixId => requestedMatrixIdsRef.current.add(matrixId));
            resolveMatrixIds({matrixIds});
        }
    }, [displayedRooms, matrixIdToUserIdMap, resolveMatrixIds]);

    const handleRoomListScroll = useCallback(() => {
        if (searchTerm || !hasMoreRooms || !onLoadMoreRooms || isLoadingMoreRooms) return;

        const container = roomsListRef.current;
        if (!container) return;

        const distanceToBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        if (distanceToBottom > ROOM_LIST_LOAD_MORE_THRESHOLD_PX) return;
        if (
            loadRequestTargetRoomCountRef.current !== null &&
            roomsWithLastMessages.length < loadRequestTargetRoomCountRef.current
        ) return;
        if (lastLoadRequestRoomCountRef.current === roomsWithLastMessages.length) return;

        lastLoadRequestRoomCountRef.current = roomsWithLastMessages.length;
        loadRequestTargetRoomCountRef.current = roomsWithLastMessages.length + ROOM_LIST_LOAD_MORE_BATCH_SIZE;
        setIsLoadingMoreRooms(true);
        Promise.resolve(onLoadMoreRooms()).finally(() => {
            setIsLoadingMoreRooms(false);
        });
    }, [searchTerm, hasMoreRooms, onLoadMoreRooms, isLoadingMoreRooms, roomsWithLastMessages.length]);

    useEffect(() => {
        if (
            loadRequestTargetRoomCountRef.current !== null &&
            roomsWithLastMessages.length >= loadRequestTargetRoomCountRef.current
        ) {
            lastLoadRequestRoomCountRef.current = null;
            loadRequestTargetRoomCountRef.current = null;
        }
    }, [roomsWithLastMessages.length]);

    useEffect(() => {
        return () => {
            lastLoadRequestRoomCountRef.current = null;
            loadRequestTargetRoomCountRef.current = null;
        };
    }, []);

    useEffect(() => {
        handleRoomListScroll();
    }, [displayedRooms.length, handleRoomListScroll]);

    const renderEmptyState = () => {
        if (!roomsSearchResults.length && usersWithoutConversation.length) {
            return (
                <div className="p-4 text-center text-ink-muted">
                    {usersWithoutConversation?.map((user: UserDirectoryItem) => (
                        <NewRoomItem
                            key={user.user_id}
                            user={user}
                            status={allStatuses?.find(u => String(u.userId) == getUserId(user.user_id))}
                            isSelected={false}
                            onClick={() => {onNewConversationClick(user)}} />
                    ))}
                </div>
            );
        }

        return (
            <div className="p-4 text-center text-ink-muted flex-col justify-center items-center flex gap-2 mt-4 text-sm">
                <MessageSquare />
                <p>No conversations matched your search</p>
            </div>
        );
    };

    const filterEmptyStateMessage: Partial<Record<RoomFilter, string>> = {
        unreads: 'No unread conversations',
        groups: 'No group conversations yet',
        direct: 'No direct conversations yet',
    };

    // 'all' is deliberately left out: an empty list there is indistinguishable
    // from one that has not finished loading, and a placeholder would flash on
    // every cold start.
    const renderFilterEmptyState = () => {
        const message = filterEmptyStateMessage[activeFilter];
        if (!message) return null;

        return (
            <div className="p-4 text-center text-ink-muted flex-col justify-center items-center flex gap-2 mt-4 text-sm">
                <MessageSquare />
                <p>{message}</p>
            </div>
        );
    };

    return (
        <section aria-label="Chat" className="w-panel shrink-0 bg-surface border-0 border-r border-solid border-line flex flex-col">
            {/* Header */}
            <div className="px-4 pb-2">
                { !inputFocus && <div className="flex h-header items-center justify-between">
                    <h2 className="m-0 text-title text-ink">Chat</h2>
                    {/* Create Room Button */}
                    <button
                        type="button"
                        aria-label="New chat"
                        onClick={onCreateRoom}
                        className="flex size-8 cursor-pointer items-center justify-center rounded-control bg-brand text-white hover:bg-brand-hover"
                    >
                        <PencilLine size={16} className={'!p-0 !text-current'}/>
                    </button>
                </div>}

                <div className={`${inputFocus ? 'mt-3' : ''} flex items-center gap-2`}>
                    {searchTerm && <PhoneHQButton onClick={() => {
                        setInputFocus(false)
                        setSearchTerm('');
                        setRoomsSearchResults([]);
                        setUsersWithoutConversation([]);
                    }} variant={'tertiary'} className={'px-0'}>
                        <ChevronLeft size={20}/>

                    </PhoneHQButton>}

                    <div className="relative w-full">
                        <PhoneHQInput
                        type='search'
                        onFocus={() => setInputFocus(true)}
                        onBlur={() => {
                            // Leaving an empty search box must bring the header
                            // (and its new conversation button) back: the reset
                            // effect below only runs when the term itself changes.
                            if (!searchTerm) setInputFocus(false);
                        }}
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder="Search..."
                            aria-label="Search chats"
                            className="w-full pl-9 !border-0 !bg-surface-muted hover:!bg-line"

                        />
                        <div className="pointer-events-none absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-muted">
                            <Search className={'!p-0 !text-current'} size={16} />
                        </div>
                    </div>
                </div>

                {!searchTerm && (
                    <div className="mt-2 flex flex-wrap items-center gap-1" role="tablist" aria-label="Filter chats">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeFilter === 'all'}
                            onClick={() => setActiveFilter('all')}
                            className={`h-[30px] cursor-pointer rounded-full px-3 text-footnote ${activeFilter === 'all' ? 'bg-brand-tint font-semibold text-brand-ink' : 'font-medium text-ink-muted hover:bg-surface-muted'}`}
                        >
                            All
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeFilter === 'groups'}
                            onClick={() => setActiveFilter('groups')}
                            className={`h-[30px] cursor-pointer rounded-full px-3 text-footnote ${activeFilter === 'groups' ? 'bg-brand-tint font-semibold text-brand-ink' : 'font-medium text-ink-muted hover:bg-surface-muted'}`}
                        >
                            Groups
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeFilter === 'direct'}
                            onClick={() => setActiveFilter('direct')}
                            className={`h-[30px] cursor-pointer rounded-full px-3 text-footnote ${activeFilter === 'direct' ? 'bg-brand-tint font-semibold text-brand-ink' : 'font-medium text-ink-muted hover:bg-surface-muted'}`}
                        >
                            Directs
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeFilter === 'unreads'}
                            onClick={() => setActiveFilter('unreads')}
                            className={`h-[30px] cursor-pointer rounded-full px-3 text-footnote ${activeFilter === 'unreads' ? 'bg-brand-tint font-semibold text-brand-ink' : 'font-medium text-ink-muted hover:bg-surface-muted'}`}
                        >
                            Unread
                        </button>
                    </div>
                )}
            </div>

            {/* Room List */}
            <div ref={roomsListRef} onScroll={handleRoomListScroll} className="flex-1 overflow-y-auto">
                {roomsSearchResults.length === 0 && searchTerm ? (
                    renderEmptyState()
                ) : (
                        <div className="flex flex-col px-2 pb-2">
                        {displayedRooms.length === 0 && renderFilterEmptyState()}
                        {displayedRooms.map((roomWithLastMessage: RoomWithLastMessage) => (
                            <RoomItem
                                key={roomWithLastMessage.roomId}
                                roomWithLastMessage={roomWithLastMessage}
                                isSelected={
                                    selectedRoomId === roomWithLastMessage.roomId ||
                                    (roomWithLastMessage.isLocal === true && (
                                        !selectedRoomId ||
                                        matrixService.getMappedRealRoomId(roomWithLastMessage.roomId) === selectedRoomId
                                    ))
                                }
                                onClick={() => {
                                    if (roomWithLastMessage.isLocal) return;
                                    onSelectRoom(roomWithLastMessage.roomId);
                                }}
                                statuses={allStatuses}
                                getUserId={getUserId}
                                getRoomUserAvatar={getRoomUserAvatar}
                                currentUserAvatar={currentUserAvatar}
                            />
                        ))}
                        {isLoadingMoreRooms && (
                            <div className="py-3 text-center text-footnote text-ink-muted">
                                Loading more conversations...
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default RoomList;
